import { useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Loader2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "../providers/supabase/supabase";
import type { AgentCapability, AgentInstance, AgentTemplate } from "./useAgentHub";

/**
 * Wizard de ativação (3 passos) e edição de um agente.
 *
 * O usuário NÃO edita o prompt base — ele escreve o contexto pessoal
 * (custom_instructions) e liga/desliga capacidades. O prompt final é montado
 * em runtime pelo worker.
 */

const CATEGORY_LABEL: Record<AgentCapability["category"], string> = {
  crm: "CRM",
  servopa: "Servopa",
  google: "Google",
  comms: "Comunicação",
  gestao: "Equipe",
  campanhas: "Metas e campanhas",
  seguros: "Seguros",
};

const DIAS = [
  { iso: 1, label: "Seg" }, { iso: 2, label: "Ter" }, { iso: 3, label: "Qua" },
  { iso: 4, label: "Qui" }, { iso: 5, label: "Sex" }, { iso: 6, label: "Sáb" },
  { iso: 7, label: "Dom" },
];

type Props = {
  template: AgentTemplate;
  instance?: AgentInstance;
  capabilities: AgentCapability[];
  connectedProviders: Set<string>;
  crmUserId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (instanceId: number) => void;
};

export const AtivarAgenteDialog = ({
  template, instance, capabilities, connectedProviders, crmUserId, open, onOpenChange, onSaved,
}: Props) => {
  const isEdit = !!instance;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [instructions, setInstructions] = useState(instance?.custom_instructions ?? "");
  const [enabled, setEnabled] = useState<string[]>(
    instance?.enabled_capabilities ?? template.default_capabilities ?? [],
  );
  const schedule = { ...(template.default_schedule ?? {}), ...(instance?.schedule_overrides ?? {}) };
  const [briefingHour, setBriefingHour] = useState<number | null>(schedule.briefing_hour ?? 8);
  const [briefingDays, setBriefingDays] = useState<number[]>(schedule.briefing_days ?? [1, 2, 3, 4, 5]);

  const byCategory = useMemo(() => {
    // Cada template só oferece as categorias dele: o de Gestão não pode expor
    // "Fila de ações" nem "Suas vendas", que são operação individual do
    // vendedor — e com data_scope='team' virariam a fila do time inteiro.
    const permitidas = template.allowed_categories;
    const doTemplate = permitidas
      ? capabilities.filter((c) => permitidas.includes(c.category))
      : capabilities;

    const groups = new Map<AgentCapability["category"], AgentCapability[]>();
    for (const cap of doTemplate) {
      const list = groups.get(cap.category) ?? [];
      list.push(cap);
      groups.set(cap.category, list);
    }
    return [...groups.entries()];
  }, [capabilities, template.allowed_categories]);

  const isBlocked = (cap: AgentCapability) =>
    cap.requires_connection === "google" && !connectedProviders.has("google");

  const toggle = (cap: AgentCapability) => {
    if (isBlocked(cap)) return;
    setEnabled((prev) =>
      prev.includes(cap.key) ? prev.filter((k) => k !== cap.key) : [...prev, cap.key],
    );
  };

  const toggleDay = (iso: number) =>
    setBriefingDays((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso].sort(),
    );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Capacidade bloqueada por falta de conexão nunca é salva ligada.
      const usable = enabled.filter((key) => {
        const cap = capabilities.find((c) => c.key === key);
        return cap && !isBlocked(cap);
      });

      const payload = {
        // Nome fixo: quem batiza o agente é o template. Deixar o usuário
        // escolher produzia "Vendas" e "Gerência" lado a lado com o de Seguros
        // também chamado "Vendas" — dois cards indistinguíveis na mesma tela.
        // Instância que já tem nome guardado fica com ele; o campo é que saiu.
        display_name: instance?.display_name ?? null,
        custom_instructions: instructions.trim() || null,
        enabled_capabilities: usable,
        schedule_overrides:
          briefingHour == null
            ? { briefing_hour: null }
            : { briefing_hour: briefingHour, briefing_days: briefingDays },
      };

      if (isEdit) {
        const { error: updateError } = await supabase
          .from("agent_instances")
          .update(payload)
          .eq("id", instance.id);
        if (updateError) throw updateError;
        onSaved(instance.id);
      } else {
        const { data, error: insertError } = await supabase
          .from("agent_instances")
          .insert({
            user_id: crmUserId,
            template_slug: template.slug,
            status: "draft",
            ...payload,
          })
          .select("id")
          .single();
        if (insertError) throw insertError;
        onSaved(data.id);
      }
      onOpenChange(false);
    } catch (err) {
      console.error("Erro ao salvar agente:", err);
      setError("Não foi possível salvar o agente.");
    } finally {
      setSaving(false);
    }
  };

  const steps = ["Identidade", "Capacidades", "Rotina"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span aria-hidden>{template.emoji}</span>
            {isEdit ? `Editar ${template.name}` : `Ativar ${template.name}`}
          </DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>

        {/* Trilha dos passos */}
        <div className="flex items-center gap-2 text-xs">
          {steps.map((label, index) => (
            <div key={label} className="flex items-center gap-2">
              <span
                className={
                  index === step
                    ? "font-semibold text-primary"
                    : index < step
                      ? "text-muted-foreground"
                      : "text-muted-foreground/60"
                }
              >
                {index + 1}. {label}
              </span>
              {index < steps.length - 1 && <span className="text-muted-foreground/40">›</span>}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="agent-instructions" className="flex items-center gap-1.5">
                <PenLine className="h-3.5 w-3.5" />
                Informações sobre você
              </Label>
              <Textarea
                id="agent-instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder={
                  "Ex.: meus clientes são maioria PJ de Curitiba, prefiro ligar de manhã, " +
                  "e sempre ofereço imóvel antes de veículo."
                }
              />
              <p className="text-xs text-muted-foreground">
                Esse contexto entra em toda conversa. {instructions.length}/2000
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5 py-2">
            {byCategory.map(([category, caps]) => (
              <div key={category} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {CATEGORY_LABEL[category]}
                </p>
                <div className="space-y-2">
                  {caps.map((cap) => {
                    const blocked = isBlocked(cap);
                    return (
                      <div
                        key={cap.key}
                        className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
                          blocked ? "opacity-60" : ""
                        }`}
                      >
                        <div className="min-w-0 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium">{cap.name}</span>
                            {cap.is_write && (
                              <Badge variant="secondary" className="text-[10px]">
                                pede confirmação
                              </Badge>
                            )}
                            {blocked && (
                              <Badge variant="outline" className="text-[10px]">
                                requer Google
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{cap.description}</p>
                          {blocked && (
                            <p className="text-xs text-amber-600">
                              A conexão com o Google ainda não está disponível.
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={enabled.includes(cap.key) && !blocked}
                          disabled={blocked}
                          onCheckedChange={() => toggle(cap)}
                          aria-label={cap.name}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Briefing diário</Label>
              <Select
                value={briefingHour == null ? "off" : String(briefingHour)}
                onValueChange={(v) => setBriefingHour(v === "off" ? null : Number(v))}
              >
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Sem briefing automático</SelectItem>
                  {Array.from({ length: 15 }, (_, i) => i + 6).map((hour) => (
                    <SelectItem key={hour} value={String(hour)}>
                      Todo dia às {String(hour).padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {briefingHour != null && (
              <div className="space-y-2">
                <Label>Dias do briefing</Label>
                <div className="flex flex-wrap gap-2">
                  {DIAS.map((dia) => (
                    <Button
                      key={dia.iso}
                      type="button"
                      size="sm"
                      variant={briefingDays.includes(dia.iso) ? "default" : "outline"}
                      onClick={() => toggleDay(dia.iso)}
                    >
                      {dia.label}
                    </Button>
                  ))}
                </div>
                {briefingDays.length === 0 && (
                  <p className="text-xs text-amber-600">
                    Sem dias selecionados, o briefing não é enviado.
                  </p>
                )}
              </div>
            )}

            <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Resumo</p>
              <p>Nome: {instance?.display_name || template.name}</p>
              <p>Capacidades ligadas: {enabled.length}</p>
              <p>
                Briefing:{" "}
                {briefingHour == null
                  ? "desligado"
                  : `${String(briefingHour).padStart(2, "0")}:00 · ${
                      briefingDays.length === 0
                        ? "nenhum dia"
                        : DIAS.filter((d) => briefingDays.includes(d.iso)).map((d) => d.label).join(", ")
                    }`}
              </p>
              {!isEdit && (
                <p className="pt-1">
                  Após a criação, ative o agente. O briefing é enviado no chat e por e-mail.
                </p>
              )}
            </div>
          </div>
        )}

        {error && (
          <p className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))}
            disabled={saving}
          >
            {step === 0 ? "Cancelar" : <><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</>}
          </Button>
          {step < steps.length - 1 ? (
            <Button type="button" onClick={() => setStep(step + 1)}>
              Continuar <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvando…</>
              ) : (
                <><Check className="h-4 w-4 mr-1" /> {isEdit ? "Salvar" : "Criar agente"}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
